import React, { useState, useEffect, useRef } from 'react';

/**
 * Select personalizado pequeño para horarios
 */
const SmallTimeSelect = ({ value, onChange, options, className = "" }) => {
  const [isOpen, setIsOpen] = useState(false);
  const selectRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (selectRef.current && !selectRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div ref={selectRef} className={`relative ${className}`}>
      <div
        onClick={() => setIsOpen(!isOpen)}
        className="h-5 bg-gray-700 border border-gray-600 rounded px-1 text-white text-xs cursor-pointer flex items-center justify-between focus:border-green-500 transition-colors"
        style={{ lineHeight: '18px' }}
      >
        <span className="text-xs" style={{ lineHeight: '18px' }}>{value}</span>
        <svg className="w-2 h-2 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
        </svg>
      </div>
      
      {isOpen && (
        <div className="absolute top-5 left-0 right-0 bg-gray-700 border border-gray-600 rounded shadow-lg max-h-32 overflow-y-auto z-50">
          {options.map(option => (
            <div
              key={option.value}
              onClick={() => {
                onChange({ target: { value: option.value } });
                setIsOpen(false);
              }}
              className="px-1 py-0.5 text-xs text-white hover:bg-gray-600 cursor-pointer"
            >
              {option.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * Componente compacto para seleccionar horarios de atención del taller
 * Versión simplificada estilo Business Hours para modales
 */
export const HorarioSelectorCompact = ({ 
  name = "sucursal_horario", 
  defaultValue = null,
  onChange = () => {},
  fullWidth = false
}) => {
  // Configuración de horarios por defecto
  const HORARIOS_DEFAULT = {
    MAÑANA_INICIO: "09:00",
    MAÑANA_FIN: "13:00", 
    TARDE_INICIO: "14:00",
    TARDE_FIN: "18:00"
  };

  const diasSemana = [
    { key: 'lunes', label: 'Lunes' },
    { key: 'martes', label: 'Martes' },
    { key: 'miercoles', label: 'Miércoles' },
    { key: 'jueves', label: 'Jueves' },
    { key: 'viernes', label: 'Viernes' },
    { key: 'sabado', label: 'Sábado' },
    { key: 'domingo', label: 'Domingo' }
  ];

  // Estructura inicial de horarios
  const horarioInicial = {
    lunes: { activo: true, mañana: { activo: true, inicio: HORARIOS_DEFAULT.MAÑANA_INICIO, fin: HORARIOS_DEFAULT.MAÑANA_FIN }, tarde: { activo: true, inicio: HORARIOS_DEFAULT.TARDE_INICIO, fin: HORARIOS_DEFAULT.TARDE_FIN } },
    martes: { activo: true, mañana: { activo: true, inicio: HORARIOS_DEFAULT.MAÑANA_INICIO, fin: HORARIOS_DEFAULT.MAÑANA_FIN }, tarde: { activo: true, inicio: HORARIOS_DEFAULT.TARDE_INICIO, fin: HORARIOS_DEFAULT.TARDE_FIN } },
    miercoles: { activo: true, mañana: { activo: true, inicio: HORARIOS_DEFAULT.MAÑANA_INICIO, fin: HORARIOS_DEFAULT.MAÑANA_FIN }, tarde: { activo: true, inicio: HORARIOS_DEFAULT.TARDE_INICIO, fin: HORARIOS_DEFAULT.TARDE_FIN } },
    jueves: { activo: true, mañana: { activo: true, inicio: HORARIOS_DEFAULT.MAÑANA_INICIO, fin: HORARIOS_DEFAULT.MAÑANA_FIN }, tarde: { activo: true, inicio: HORARIOS_DEFAULT.TARDE_INICIO, fin: HORARIOS_DEFAULT.TARDE_FIN } },
    viernes: { activo: true, mañana: { activo: true, inicio: HORARIOS_DEFAULT.MAÑANA_INICIO, fin: HORARIOS_DEFAULT.MAÑANA_FIN }, tarde: { activo: true, inicio: HORARIOS_DEFAULT.TARDE_INICIO, fin: HORARIOS_DEFAULT.TARDE_FIN } },
    sabado: { activo: true, mañana: { activo: true, inicio: HORARIOS_DEFAULT.MAÑANA_INICIO, fin: HORARIOS_DEFAULT.MAÑANA_FIN }, tarde: { activo: false, inicio: "", fin: "" } },
    domingo: { activo: false, mañana: { activo: false, inicio: "", fin: "" }, tarde: { activo: false, inicio: "", fin: "" } }
  };

  const [horarios, setHorarios] = useState(() => {
    if (defaultValue) {
      try {
        return typeof defaultValue === 'string' ? JSON.parse(defaultValue) : defaultValue;
      } catch {
        return horarioInicial;
      }
    }
    return horarioInicial;
  });

  // Actualizar horarios cuando cambie defaultValue (para casos de edición)
  useEffect(() => {
    //console.log('HorarioSelectorCompact - defaultValue changed:', defaultValue);
    if (defaultValue) {
      try {
        const parsedValue = typeof defaultValue === 'string' ? JSON.parse(defaultValue) : defaultValue;
        //console.log('HorarioSelectorCompact - parsed value:', parsedValue);
        setHorarios(parsedValue);
      } catch (e) {
        console.warn('HorarioSelectorCompact - Error al parsear defaultValue:', e);
        //console.log('HorarioSelectorCompact - defaultValue original:', defaultValue);
        setHorarios(horarioInicial);
      }
    } else {
      //console.log('HorarioSelectorCompact - No defaultValue, using horarioInicial');
      setHorarios(horarioInicial);
    }
  }, [defaultValue]);

  // Actualizar el padre cuando cambien los horarios
  useEffect(() => {
    onChange(horarios);
  }, [horarios]);

  // Función para alternar día activo/inactivo
  const toggleDia = (dia) => {
    setHorarios(prev => ({
      ...prev,
      [dia]: {
        ...prev[dia],
        activo: !prev[dia].activo
      }
    }));
  };

  // Función para alternar turno (mañana/tarde)
  const toggleTurno = (dia, turno) => {
    setHorarios(prev => ({
      ...prev,
      [dia]: {
        ...prev[dia],
        [turno]: {
          ...prev[dia][turno],
          activo: !prev[dia][turno].activo
        }
      }
    }));
  };

  // Función para actualizar horario específico
  const updateHorario = (dia, turno, campo, valor) => {
    setHorarios(prev => ({
      ...prev,
      [dia]: {
        ...prev[dia],
        [turno]: {
          ...prev[dia][turno],
          [campo]: valor
        }
      }
    }));
  };

  // Generar opciones de horas (00-23)
  const generateHourOptions = () => {
    const hours = [];
    for (let i = 0; i < 24; i++) {
      const hour = i.toString().padStart(2, '0');
      hours.push({ value: `${hour}:00`, label: `${hour}:00` });
      hours.push({ value: `${hour}:30`, label: `${hour}:30` });
    }
    return hours;
  };

  const timeOptions = generateHourOptions();

  // Aplicar configuración específica
  const aplicarConfiguracion = (tipo) => {
    if (tipo === 'lun-vie') {
      // Lunes a Viernes completo, Sábado y Domingo cerrados
      const nuevosHorarios = {
        lunes: { activo: true, mañana: { activo: true, inicio: HORARIOS_DEFAULT.MAÑANA_INICIO, fin: HORARIOS_DEFAULT.MAÑANA_FIN }, tarde: { activo: true, inicio: HORARIOS_DEFAULT.TARDE_INICIO, fin: HORARIOS_DEFAULT.TARDE_FIN } },
        martes: { activo: true, mañana: { activo: true, inicio: HORARIOS_DEFAULT.MAÑANA_INICIO, fin: HORARIOS_DEFAULT.MAÑANA_FIN }, tarde: { activo: true, inicio: HORARIOS_DEFAULT.TARDE_INICIO, fin: HORARIOS_DEFAULT.TARDE_FIN } },
        miercoles: { activo: true, mañana: { activo: true, inicio: HORARIOS_DEFAULT.MAÑANA_INICIO, fin: HORARIOS_DEFAULT.MAÑANA_FIN }, tarde: { activo: true, inicio: HORARIOS_DEFAULT.TARDE_INICIO, fin: HORARIOS_DEFAULT.TARDE_FIN } },
        jueves: { activo: true, mañana: { activo: true, inicio: HORARIOS_DEFAULT.MAÑANA_INICIO, fin: HORARIOS_DEFAULT.MAÑANA_FIN }, tarde: { activo: true, inicio: HORARIOS_DEFAULT.TARDE_INICIO, fin: HORARIOS_DEFAULT.TARDE_FIN } },
        viernes: { activo: true, mañana: { activo: true, inicio: HORARIOS_DEFAULT.MAÑANA_INICIO, fin: HORARIOS_DEFAULT.MAÑANA_FIN }, tarde: { activo: true, inicio: HORARIOS_DEFAULT.TARDE_INICIO, fin: HORARIOS_DEFAULT.TARDE_FIN } },
        sabado: { activo: false, mañana: { activo: false, inicio: "", fin: "" }, tarde: { activo: false, inicio: "", fin: "" } },
        domingo: { activo: false, mañana: { activo: false, inicio: "", fin: "" }, tarde: { activo: false, inicio: "", fin: "" } }
      };
      setHorarios(nuevosHorarios);
    } else if (tipo === 'lun-sab') {
      // Toggle sábado - si ya está activo, desactivarlo
      setHorarios(prev => ({
        ...prev,
        sabado: prev.sabado.activo 
          ? { activo: false, mañana: { activo: false, inicio: "", fin: "" }, tarde: { activo: false, inicio: "", fin: "" } }
          : { activo: true, mañana: { activo: true, inicio: HORARIOS_DEFAULT.MAÑANA_INICIO, fin: HORARIOS_DEFAULT.MAÑANA_FIN }, tarde: { activo: true, inicio: HORARIOS_DEFAULT.TARDE_INICIO, fin: HORARIOS_DEFAULT.TARDE_FIN } }
      }));
    } else if (tipo === 'todos') {
      // Toggle domingo - si ya está activo, desactivarlo
      setHorarios(prev => ({
        ...prev,
        domingo: prev.domingo.activo 
          ? { activo: false, mañana: { activo: false, inicio: "", fin: "" }, tarde: { activo: false, inicio: "", fin: "" } }
          : { activo: true, mañana: { activo: true, inicio: HORARIOS_DEFAULT.MAÑANA_INICIO, fin: HORARIOS_DEFAULT.MAÑANA_FIN }, tarde: { activo: true, inicio: HORARIOS_DEFAULT.TARDE_INICIO, fin: HORARIOS_DEFAULT.TARDE_FIN } }
      }));
    }
  };

  return (
    <div className="space-y-3">
      {/* Plantillas rápidas */}
      <div className="flex gap-1 justify-center">
        <button
          type="button"
          onClick={() => aplicarConfiguracion('lun-vie')}
          className="px-2 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded text-xs font-medium transition-colors"
        >
          Lunes a Viernes
        </button>
        <button
          type="button"
          onClick={() => aplicarConfiguracion('lun-sab')}
          className="px-2 py-1 bg-purple-600 hover:bg-purple-700 text-white rounded text-xs font-medium transition-colors"
        >
          {horarios.sabado.activo ? '- Sábado' : '+ Sábado'}
        </button>
        <button
          type="button"
          onClick={() => aplicarConfiguracion('todos')}
          className="px-2 py-1 bg-green-600 hover:bg-green-700 text-white rounded text-xs font-medium transition-colors"
        >
          {horarios.domingo.activo ? '- Domingo' : '+ Domingo'}
        </button>
      </div>

      {/* Lista de días con horarios - estilo Business hours en grid */}
      <div className={`grid gap-3 ${
        fullWidth 
          ? 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4' 
          : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'
      }`}>
        {diasSemana.map(({ key, label }) => (
          <div key={key} className="border border-gray-600 rounded-lg p-3 bg-gray-800/50">
            {/* Switch y nombre del día */}
            <div className="flex items-center gap-3 mb-3">
              <div 
                className={`w-5 h-5 rounded border-2 transition-all duration-200 flex items-center justify-center cursor-pointer ${
                  horarios[key].activo 
                    ? 'bg-blue-500 border-blue-500' 
                    : 'bg-gray-700 border-gray-500 hover:border-blue-400'
                }`}
                onClick={() => toggleDia(key)}
              >
                {horarios[key].activo && (
                  <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                )}
              </div>
              <span className="ml-3 text-sm font-medium text-white">{label}</span>
            </div>

            {/* Horarios */}
            {horarios[key].activo && (
              <div className="space-y-2">
                {/* Turno Mañana */}
                <div className="grid grid-cols-12 gap-1 items-center text-xs">
                  <div className="col-span-1 flex items-center">
                    <div 
                      className={`w-4 h-4 rounded border-2 transition-all duration-200 flex items-center justify-center cursor-pointer ${
                        horarios[key].mañana.activo 
                          ? 'bg-green-500 border-green-500' 
                          : 'bg-gray-700 border-gray-500 hover:border-green-400'
                      }`}
                      onClick={() => toggleTurno(key, 'mañana')}
                    >
                      {horarios[key].mañana.activo && (
                        <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </div>
                  </div>
                  {horarios[key].mañana.activo && (
                    <>
                      <SmallTimeSelect
                        value={horarios[key].mañana.inicio}
                        onChange={(e) => updateHorario(key, 'mañana', 'inicio', e.target.value)}
                        options={timeOptions}
                        className="col-span-5"
                      />
                      <span className="col-span-1 text-gray-400 text-center text-xs">a</span>
                      <SmallTimeSelect
                        value={horarios[key].mañana.fin}
                        onChange={(e) => updateHorario(key, 'mañana', 'fin', e.target.value)}
                        options={timeOptions}
                        className="col-span-5"
                      />
                    </>
                  )}
                </div>

                {/* Turno Tarde */}
                <div className="grid grid-cols-12 gap-1 items-center text-xs">
                  <div className="col-span-1 flex items-center">
                    <div 
                      className={`w-4 h-4 rounded border-2 transition-all duration-200 flex items-center justify-center cursor-pointer ${
                        horarios[key].tarde.activo 
                          ? 'bg-orange-500 border-orange-500' 
                          : 'bg-gray-700 border-gray-500 hover:border-orange-400'
                      }`}
                      onClick={() => toggleTurno(key, 'tarde')}
                    >
                      {horarios[key].tarde.activo && (
                        <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </div>
                  </div>
                  {horarios[key].tarde.activo && (
                    <>
                      <SmallTimeSelect
                        value={horarios[key].tarde.inicio}
                        onChange={(e) => updateHorario(key, 'tarde', 'inicio', e.target.value)}
                        options={timeOptions}
                        className="col-span-5"
                      />
                      <span className="col-span-1 text-gray-400 text-center text-xs">a</span>
                      <SmallTimeSelect
                        value={horarios[key].tarde.fin}
                        onChange={(e) => updateHorario(key, 'tarde', 'fin', e.target.value)}
                        options={timeOptions}
                        className="col-span-5"
                      />
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Input hidden para envío del formulario */}
      <input
        type="hidden"
        name={name}
        value={JSON.stringify(horarios)}
      />
    </div>
  );
};

export default HorarioSelectorCompact;